import { useEffect, useRef } from 'react';

type Options = {
  /** Lock fires this many ms after the last user activity. */
  timeoutMs: number;
  /** Warning fires this many ms after the last user activity, before lock. Must be < timeoutMs. */
  warningMs: number;
  /** Called when warning should be shown. */
  onWarning: () => void;
  /** Called when lock should engage. */
  onLock: () => void;
  /** Called whenever activity resets the timer (e.g. so a visible warning can be dismissed). */
  onActivity?: () => void;
  /** When false the hook tears down its listeners and timers (e.g. after the lock engages, or on sign-out). */
  enabled: boolean;
  /**
   * localStorage key used to broadcast activity across tabs. Activity in any
   * tab sharing this key resets the timer in every other tab.
   */
  storageKey?: string;
};

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousedown',
  'mousemove',
  'keydown',
  'touchstart',
  'pointerdown',
  'scroll',
  'wheel',
];

const ACTIVITY_DEBOUNCE_MS = 250;

/**
 * Tracks user activity and fires onWarning then onLock when the user goes
 * idle for `timeoutMs`. Cross-tab via a localStorage timestamp key. Survives
 * tab-backgrounding throttling by recording the last-activity wall-clock time
 * and re-checking on `visibilitychange`.
 *
 * The hook does NOT manage lock state itself — callers handle that. This
 * lets the hook be lifted out and unit-tested independent of the overlay.
 */
export function useInactivityTimer({
  timeoutMs,
  warningMs,
  onWarning,
  onLock,
  onActivity,
  enabled,
  storageKey = 'chiroup.lastActivity',
}: Options) {
  const lastActivityRef = useRef<number>(Date.now());
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastBroadcastRef = useRef<number>(0);
  const onWarningRef = useRef(onWarning);
  const onLockRef = useRef(onLock);
  const onActivityRef = useRef(onActivity);

  onWarningRef.current = onWarning;
  onLockRef.current = onLock;
  onActivityRef.current = onActivity;

  useEffect(() => {
    if (!enabled) return;

    const clearTimers = () => {
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
      warningTimerRef.current = null;
      lockTimerRef.current = null;
    };

    const scheduleTimers = () => {
      clearTimers();

      const sinceActivity = Date.now() - lastActivityRef.current;
      const remainingToWarn = Math.max(0, warningMs - sinceActivity);
      const remainingToLock = Math.max(0, timeoutMs - sinceActivity);

      if (remainingToLock === 0) {
        onLockRef.current();

        return;
      }

      if (remainingToWarn < remainingToLock) {
        warningTimerRef.current = setTimeout(() => {
          onWarningRef.current();
        }, remainingToWarn);
      }

      lockTimerRef.current = setTimeout(() => {
        onLockRef.current();
      }, remainingToLock);
    };

    const recordActivity = (broadcast: boolean) => {
      const now = Date.now();
      lastActivityRef.current = now;
      onActivityRef.current?.();

      // Throttle cross-tab broadcasts — one storage write per second is plenty.
      if (broadcast && now - lastBroadcastRef.current > 1000) {
        lastBroadcastRef.current = now;
        try {
          localStorage.setItem(storageKey, String(now));
        } catch {
          // localStorage may be unavailable (private mode, quota) — degrade gracefully.
        }
      }

      scheduleTimers();
    };

    let activityDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    const handleActivity = () => {
      if (activityDebounceTimer) return;
      activityDebounceTimer = setTimeout(() => {
        activityDebounceTimer = null;
        recordActivity(true);
      }, ACTIVITY_DEBOUNCE_MS);
    };

    const handleStorage = (e: StorageEvent) => {
      if (e.key !== storageKey || !e.newValue) return;
      const remoteTs = Number(e.newValue);

      if (!Number.isFinite(remoteTs) || remoteTs <= lastActivityRef.current) return;
      lastActivityRef.current = remoteTs;
      onActivityRef.current?.();
      scheduleTimers();
    };

    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      // setTimeout is throttled in background tabs — when we come back to the
      // foreground, recompute against wall-clock time. If we've actually been
      // idle past the threshold, fire the lock immediately rather than waiting
      // for the throttled timer to catch up.
      const sinceActivity = Date.now() - lastActivityRef.current;

      if (sinceActivity >= timeoutMs) {
        clearTimers();
        onLockRef.current();

        return;
      }

      scheduleTimers();
    };

    // Initial schedule from "now."
    lastActivityRef.current = Date.now();
    scheduleTimers();

    ACTIVITY_EVENTS.forEach(evt => {
      window.addEventListener(evt, handleActivity, { passive: true });
    });
    window.addEventListener('storage', handleStorage);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, handleActivity));
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisibility);
      if (activityDebounceTimer) clearTimeout(activityDebounceTimer);
      clearTimers();
    };
  }, [enabled, timeoutMs, warningMs, storageKey]);
}
